import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useAuth } from "../../hooks/useAuth";
import { useSharePoint } from "../../hooks/useSharePoint";
import {
  Card,
  Typography,
  Input,
  Select,
  Alert,
  Row,
  Col,
  Button,
  message,
  Modal,
} from "antd";
import {
  ArrowRightOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useTender } from "../../contexts/TenderContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useUserRole } from "../../hooks/useUserRole";
import { useOpportunities } from "../../hooks/useOpportunities";
import { useDevMode } from "../../contexts/DevModeContext";
import MainLayout from "../layout/MainLayout";
import { REGIONS, COLORS } from "../../utils/constants";
import {
  saveTenderToStorage,
  prepareTenderForAPI,
  transformFromPayload,
} from "../../utils/storage";
import { itemLibraryApi } from "../../api/itemLibrary";
import { billOfQuantitiesApi } from "../../api/billOfQuantities";
import { createVersionPayload } from "../../utils/boqPayloadBuilder";
import { useCreateBillOfQuantities } from "../../hooks/useBillOfQuantities";
import { TenderState } from "../../types/tender.types";
import { OpportunityRecord } from "../../pages/tender/helper/opportunities";
import OpportunityImportCard from "./OpportunityImportCard";
import { Layers3, Route } from "lucide-react";

// Domain fields carried by each job type - used to scope what an opportunity
// import merges into the shared TenderState, so importing one job type never
// clobbers data already imported for the other.
const ROADS_AND_SEWERS_FIELDS: (keyof TenderState)[] = [
  "foulSewers",
  "pumpStation",
  "risingMain",
  "stormSewers",
  "boxCulverts",
  "cellularAttenuation",
  "concAttenuation",
  "pondsSwales",
  "weholiteAttenuation",
  "roads",
  "mainServices",
  "substation",
  "preliminaryItems",
];
const GROUNDWORKS_FIELDS: (keyof TenderState)[] = [
  "groundworks",
  "pos",
  "compound",
  "spoilReconciliation",
];

const { Title, Text } = Typography;

interface BasicTenderFormData {
  projectName: string;
  client: string;
  siteAddress: string;
  region: string;
}

const TenderDetails: React.FC = () => {
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const { state, updateField } = useTender();
  useAutoSave();
  const { user } = useAuth();
  const { role, region: userRegion, isLoading: isRoleLoading } = useUserRole();
  // `createFolder` is unused while SharePoint folder creation is commented
  // out below (now handled server-side by the backend on tender creation) -
  // left undestructured rather than removed so re-enabling it is a one-line
  // change. `isCreatingFolder` stays destructured since `canProceed` still
  // reads it.
  const { isCreatingFolder } = useSharePoint();
  const devMode = useDevMode();
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { data: opportunities = [], isLoading: opportunitiesLoading } =
    useOpportunities();
  const [importedRoadsAndSewers, setImportedRoadsAndSewers] =
    useState<OpportunityRecord | null>(null);
  const [importedGroundworks, setImportedGroundworks] =
    useState<OpportunityRecord | null>(null);
  const [activeBasicSource, setActiveBasicSource] = useState<
    "roadsAndSewers" | "groundworks" | null
  >(null);

  // No job type selected means the Import Opportunities card has nothing to
  // show - send the user back to pick one instead of rendering an empty page.
  useEffect(() => {
    if (!state.jobTypes.roadsAndSewers && !state.jobTypes.groundworks) {
      navigate("/tender/job-types");
    }
  }, [state.jobTypes.roadsAndSewers, state.jobTypes.groundworks, navigate]);

  const emptyManualDetails: BasicTenderFormData = {
    projectName: "",
    client: "",
    siteAddress: "",
    region: "",
  };
  const [manualDetailsByType, setManualDetailsByType] = useState<
    Record<"roadsAndSewers" | "groundworks", BasicTenderFormData>
  >(() => {
    // An existing tender loaded from the API (e.g. via Version History's
    // "Link Opportunity"/"Import Take-off" buttons) already has real details
    // for whichever job type(s) it already has - seed each already-selected
    // job type's card preview from those instead of showing a blank "Manual
    // Entry" card until the user clicks into it. Prefer that job type's own
    // `opportunity.groundworks`/`opportunity.roadsAndSewers` entry (set by
    // billofquantities whenever that job type's opportunity was actually
    // imported - see buildOpportunityField below) since it carries that job
    // type's own name/client/siteAddress/region even when the other job
    // type's data differs; fall back to the shared navbar
    // projectName/client/siteAddress/region (state.opportunity is never set
    // for a manually-entered tender that never had a real opportunity).
    const hasLoadedTender = !!state.projectName;
    const navbarDetails: BasicTenderFormData = {
      projectName: state.projectName || "",
      client: state.client || "",
      siteAddress: state.siteAddress || "",
      region: state.region || "",
    };
    const detailsFor = (jobType: "groundworks" | "roadsAndSewers"): BasicTenderFormData => {
      const opp = state.opportunity?.[jobType];
      if (opp) {
        return {
          projectName: opp.name || "",
          client: opp.clientName || "",
          siteAddress: opp.siteAddress || "",
          region: opp.region || "",
        };
      }
      // No per-type opportunity entry recorded yet for this job type (e.g.
      // it was just added as the second job type via /tender/job-types and
      // hasn't been imported/typed into yet). Only safe to fall back to the
      // shared navbar fields when this is the ONLY selected job type - the
      // navbar's single projectName/client/etc. belongs to whichever job
      // type was already on the tender, so reusing it here once a second,
      // still-empty job type exists would show that same text under both
      // cards instead of leaving the new one blank.
      const otherJobType = jobType === "groundworks" ? "roadsAndSewers" : "groundworks";
      const isOnlySelectedJobType = state.jobTypes[jobType] && !state.jobTypes[otherJobType];
      return isOnlySelectedJobType ? navbarDetails : { ...emptyManualDetails };
    };
    return {
      roadsAndSewers:
        hasLoadedTender && state.jobTypes.roadsAndSewers
          ? detailsFor("roadsAndSewers")
          : { ...emptyManualDetails },
      groundworks:
        hasLoadedTender && state.jobTypes.groundworks
          ? detailsFor("groundworks")
          : { ...emptyManualDetails },
    };
  });

  const createBoqMutation = useCreateBillOfQuantities();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors, isValid },
  } = useForm<BasicTenderFormData>({
    mode: "onBlur",
    defaultValues: {
      projectName: state.projectName || "",
      client: state.client,
      siteAddress: state.siteAddress || "",
      region: state.region,
    },
  });

  const watchedFields = watch();
  const isUpdatingFromForm = useRef(false);

  const bothJobTypesSelected = !!(
    state.jobTypes.groundworks && state.jobTypes.roadsAndSewers
  );

  // Effective manually-typed details for a job type: always
  // manualDetailsByType's own record for that job type, never derived from
  // which card happens to be "active" - the "keep manual details" effect
  // below keeps manualDetailsByType in sync with the form on every keystroke
  // while a card is active, so it's already current. Branching on
  // activeBasicSource here as well (using live watchedFields for whichever
  // card was active) was redundant and a source of bugs - e.g. a job type's
  // own entry could end up showing the OTHER job type's/shared navbar's
  // values depending on exactly which card happened to be active at a given
  // instant, since watchedFields is one shared form reused by both cards.
  const effectiveGroundworks = importedGroundworks ? null : manualDetailsByType.groundworks;
  const effectiveRoadsAndSewers = importedRoadsAndSewers ? null : manualDetailsByType.roadsAndSewers;

  // Once both job types have been manually named (neither imported), the
  // navbar's Tender Name/Site Address should reflect both instead of only
  // whichever card is currently active - same concatenated "Groundworks -
  // Roads & Sewers" convention as the imported-opportunity case above.
  const bothManualFilled =
    bothJobTypesSelected &&
    !importedGroundworks &&
    !importedRoadsAndSewers &&
    !!effectiveGroundworks?.projectName?.trim() &&
    !!effectiveRoadsAndSewers?.projectName?.trim();

  // Auto-save form fields into context, debounced
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isValid && Object.keys(errors).length === 0) {
        isUpdatingFromForm.current = true;
        const projectName = bothManualFilled
          ? [effectiveGroundworks!.projectName, effectiveRoadsAndSewers!.projectName]
              .map((s) => s.trim())
              .join(" - ")
          : watchedFields.projectName.trim();
        const siteAddress = bothManualFilled
          ? [effectiveGroundworks!.siteAddress, effectiveRoadsAndSewers!.siteAddress]
              .filter(Boolean)
              .map((s) => (s || "").trim())
              .join(" - ")
          : (watchedFields.siteAddress || "").trim();
        updateField("projectName", projectName);
        updateField("client", watchedFields.client.trim());
        updateField("siteAddress", siteAddress);
        updateField("region", watchedFields.region);
        validateProjectName(projectName);
        setTimeout(() => {
          isUpdatingFromForm.current = false;
        }, 100);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFields, isValid, bothManualFilled, effectiveGroundworks, effectiveRoadsAndSewers]);

  // Keep each job type's own manually-typed details around so switching
  // between cards doesn't lose whatever was typed for the other one.
  // `watch()` returns a new object every render, so this must depend on the
  // primitive fields (not `watchedFields` itself) and bail out when nothing
  // actually changed — otherwise every render produces a new saved object,
  // which triggers another render, forever.
  useEffect(() => {
    if (!activeBasicSource) return;
    const imported =
      activeBasicSource === "roadsAndSewers"
        ? importedRoadsAndSewers
        : importedGroundworks;
    if (imported) return;

    setManualDetailsByType((prev) => {
      const current = prev[activeBasicSource];
      const next = {
        projectName: watchedFields.projectName,
        client: watchedFields.client,
        siteAddress: watchedFields.siteAddress,
        region: watchedFields.region,
      };
      if (
        current.projectName === next.projectName &&
        current.client === next.client &&
        current.siteAddress === next.siteAddress &&
        current.region === next.region
      ) {
        return prev;
      }
      return { ...prev, [activeBasicSource]: next };
    });
  }, [
    watchedFields.projectName,
    watchedFields.client,
    watchedFields.siteAddress,
    watchedFields.region,
    activeBasicSource,
    importedRoadsAndSewers,
    importedGroundworks,
  ]);

  // Set region from user profile if a specific region is selected
  useEffect(() => {
    if (userRegion && userRegion !== "All Regions") {
      setValue("region", userRegion);
      updateField("region", userRegion);
    } else if (userRegion === "All Regions") {
      setValue("region", "");
      updateField("region", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRegion, setValue]);

  const validateProjectName = (projectName: string) => {
    if (!projectName.trim()) return;

    const errors = [];
    const existingTenders = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("mjEvans_tender_")) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            const tenderData = parsed.data || parsed;
            if (tenderData.projectName && tenderData.id !== state.id) {
              existingTenders.push(tenderData.projectName.toLowerCase());
            }
          } catch (e) {
            // Ignore invalid entries
          }
        }
      }
    }

    if (existingTenders.includes(projectName.toLowerCase())) {
      errors.push(
        "A tender with this project name already exists. Please choose a unique name.",
      );
    }

    setValidationErrors(errors);
  };

  const navigateToImportTakeoff = () => {
    navigate("/tender/import-takeoff");
  };

  // The user's profile region always wins when they have one set (profile
  // region locks/disables the dropdown below) - only fall back to the
  // opportunity's/manually-typed region when the profile is "All Regions".
  const resolveRegion = (candidateRegion: string) =>
    userRegion && userRegion !== "All Regions"
      ? userRegion
      : candidateRegion || state.region || REGIONS[0];

  // Numeric opportunity number for the payload's `opportunity` key - only
  // set per job type when that job type's opportunity was actually imported;
  // never set for manual entries.
  const toOppNo = (no?: string): number | undefined => {
    if (!no) return undefined;
    const n = Number(no);
    return Number.isFinite(n) ? n : undefined;
  };

  const buildOpportunityField = (): TenderState["opportunity"] => {
    const opportunity: NonNullable<TenderState["opportunity"]> = {};
    const groundworksNo = toOppNo(importedGroundworks?.no);
    if (importedGroundworks) {
      opportunity.groundworks = {
        no: groundworksNo,
        clientName: importedGroundworks.client || "",
        region: importedGroundworks.region || "",
        name: importedGroundworks.tender || "",
        siteAddress: importedGroundworks.siteAddress || "",
      };
    } else if (state.jobTypes.groundworks && effectiveGroundworks?.projectName?.trim()) {
      // Manual (non-imported) job type - still gets an entry (no `no`, since
      // there's no real opportunity number) so this job type's own name/
      // client/siteAddress/region survive round-trips the same way an
      // imported one does, instead of only ever living in the shared
      // navbar projectName/client/siteAddress.
      opportunity.groundworks = {
        clientName: effectiveGroundworks.client || "",
        region: effectiveGroundworks.region || "",
        name: effectiveGroundworks.projectName || "",
        siteAddress: effectiveGroundworks.siteAddress || "",
      };
    }
    const roadsAndSewersNo = toOppNo(importedRoadsAndSewers?.no);
    if (importedRoadsAndSewers) {
      opportunity.roadsAndSewers = {
        no: roadsAndSewersNo,
        clientName: importedRoadsAndSewers.client || "",
        region: importedRoadsAndSewers.region || "",
        name: importedRoadsAndSewers.tender || "",
        siteAddress: importedRoadsAndSewers.siteAddress || "",
      };
    } else if (state.jobTypes.roadsAndSewers && effectiveRoadsAndSewers?.projectName?.trim()) {
      opportunity.roadsAndSewers = {
        clientName: effectiveRoadsAndSewers.client || "",
        region: effectiveRoadsAndSewers.region || "",
        name: effectiveRoadsAndSewers.projectName || "",
        siteAddress: effectiveRoadsAndSewers.siteAddress || "",
      };
    }
    return Object.keys(opportunity).length > 0 ? opportunity : undefined;
  };

  const fillBasicFieldsFrom = (record: OpportunityRecord) => {
    const region = resolveRegion(record.region || "");
    // Once both job types have their own opportunity imported, the navbar's
    // Tender Name/Opportunity No/Site Address should reflect both instead of
    // whichever card happens to be active - concatenated "Groundworks - Roads
    // & Sewers" (matching the cards' own left-to-right order). Client and
    // Region stay single-source (whichever card is active), same as before.
    // Separator is " - ", NOT "/" - projectName is used as a raw path segment
    // in API calls (e.g. GET /billofquantities/{projectName}), and a literal
    // "/" there gets treated as a path separator by the backend even when
    // percent-encoded, breaking every request for that tender.
    const bothImported = !!importedRoadsAndSewers && !!importedGroundworks;
    const projectName = bothImported
      ? [importedGroundworks!.tender, importedRoadsAndSewers!.tender]
          .filter(Boolean)
          .join(" - ")
      : record.tender;
    const opportunityNo = bothImported
      ? [importedGroundworks!.no, importedRoadsAndSewers!.no]
          .filter(Boolean)
          .join(" - ")
      : record.no || "";
    const siteAddress = bothImported
      ? [importedGroundworks!.siteAddress, importedRoadsAndSewers!.siteAddress]
          .filter(Boolean)
          .join(" - ")
      : record.siteAddress || "";
    const client = record.client;
    // reset(), not four setValue() calls - reset() is react-hook-form's
    // designed-for-bulk-load API and reliably updates every Controller
    // regardless of whether that field has ever been touched/mounted before;
    // setValue() per-field is designed for "the user is editing one field"
    // and isn't guaranteed to sync a Controller that just mounted.
    reset({ projectName, client, siteAddress, region });
    updateField("projectName", projectName);
    updateField("client", client);
    updateField("siteAddress", siteAddress);
    updateField("region", region);
    updateField("opportunityNo", opportunityNo);
    updateField("opportunity", buildOpportunityField());
    setTimeout(() => trigger(), 100);
  };

  const restoreManualDetails = (
    jobType: "roadsAndSewers" | "groundworks",
  ) => {
    const saved = manualDetailsByType[jobType];
    const region = resolveRegion(saved.region);
    const siteAddress = saved.siteAddress || "";
    reset({
      projectName: saved.projectName,
      client: saved.client,
      siteAddress,
      region,
    });
    updateField("projectName", saved.projectName);
    updateField("client", saved.client);
    updateField("siteAddress", siteAddress);
    updateField("region", region);
    updateField("opportunityNo", "");
    // Don't blindly clear `opportunity` - the other job type may still be
    // imported, and its number must survive this job type going manual.
    updateField("opportunity", buildOpportunityField());
  };

  // Single place that actually fills/clears the form for whichever job type
  // is active. Both importing and clicking a card only ever change
  // `activeBasicSource`/`importedRoadsAndSewers`/`importedGroundworks` -
  // this effect is what reacts to that and fills the form, so import and
  // card-switching go through the exact same fill path instead of each
  // calling `fillBasicFieldsFrom` inline (which, for import specifically,
  // was not reliably syncing react-hook-form's Controllers - likely a
  // batching/timing quirk of updating form values from deep inside an
  // async handler versus a plain synchronous click).
  useEffect(() => {
    if (!activeBasicSource) return;
    const record =
      activeBasicSource === "roadsAndSewers"
        ? importedRoadsAndSewers
        : importedGroundworks;
    if (record) {
      fillBasicFieldsFrom(record);
    } else {
      restoreManualDetails(activeBasicSource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBasicSource, importedRoadsAndSewers, importedGroundworks]);

  const handleSwitchBasicSource = (
    jobType: "roadsAndSewers" | "groundworks",
  ) => {
    setActiveBasicSource(jobType);
  };

  // Only marks the opportunity as imported/active and fills the basic tender
  // fields (via the effect above) - no API call here. The heavier
  // foulSewers/groundworks/etc. domain-data fetch is deferred to
  // fetchImportedDomainData(), which only runs on Save & Continue/Save & Exit.
  const handleImportOpportunity = (
    jobType: "roadsAndSewers" | "groundworks",
    record: OpportunityRecord,
  ) => {
    const setImported =
      jobType === "roadsAndSewers"
        ? setImportedRoadsAndSewers
        : setImportedGroundworks;

    setActiveBasicSource(jobType);
    setImported(record);
  };

  // Clears a card's details, whether imported or manually typed. For an
  // imported card this drops the import (the effect above then reacts to
  // importedRoadsAndSewers/importedGroundworks turning null by restoring
  // that job type's - now blank - manual details). For a manual card
  // there's no imported-state change to react to, so the form/context
  // fields are reset directly here when that card is the active one.
  const handleClearCard = (jobType: "roadsAndSewers" | "groundworks") => {
    const setImported =
      jobType === "roadsAndSewers"
        ? setImportedRoadsAndSewers
        : setImportedGroundworks;
    setImported(null);
    setManualDetailsByType((prev) => ({
      ...prev,
      [jobType]: { ...emptyManualDetails },
    }));

    const newOpportunity = { ...(state.opportunity || {}) };
    delete newOpportunity[jobType];
    updateField(
      "opportunity",
      Object.keys(newOpportunity).length > 0 ? newOpportunity : undefined,
    );

    if (activeBasicSource === jobType) {
      const region = resolveRegion("");
      reset({ projectName: "", client: "", siteAddress: "", region });
      updateField("projectName", "");
      updateField("client", "");
      updateField("siteAddress", "");
      updateField("region", region);
      updateField("opportunityNo", "");
    }
  };

  // Checks (GET, before any POST) whether a BOQ already exists for this
  // tender - same "check before import" pattern as /tender-info's
  // handleImportProject - and, if so, merges its foulSewers/groundworks/etc.
  // domain data, scoped per job type. Looked up by state.projectName/
  // state.opportunityNo - the navbar's Tender Name/Opportunity No - not each
  // opportunity record's own tender/no, since once both job types are
  // imported those are the concatenated "Groundworks - Roads & Sewers"
  // values that actually identify this tender's saved BOQ.
  const fetchImportedDomainData = async (): Promise<{
    merged: Partial<TenderState>;
    alreadyImported: boolean;
    existingStatus?: string;
  }> => {
    const merged: Partial<TenderState> = {};
    const importedFields: (keyof TenderState)[] = [
      ...(importedRoadsAndSewers ? ROADS_AND_SEWERS_FIELDS : []),
      ...(importedGroundworks ? GROUNDWORKS_FIELDS : []),
    ];
    if (importedFields.length === 0) {
      return { merged, alreadyImported: false };
    }

    try {
      const response = await billOfQuantitiesApi.getByProject(
        state.projectName,
        state.opportunityNo || undefined,
      );

      // Same "any non-empty successful response means it already exists"
      // check as /tender-info's handleImportProject.
      const responseIsPresent =
        response &&
        response.success !== false &&
        typeof response === "object" &&
        Object.keys(response).length > 0;

      if (!responseIsPresent) {
        return { merged, alreadyImported: false };
      }

      const existingStatus =
        (response.data as { status?: string } | null)?.status ||
        (response as unknown as { status?: string }).status ||
        "Unknown";

      if (response.data) {
        const tenderState = transformFromPayload(response.data, {
          id: state.id,
        });
        importedFields.forEach((field) => {
          if (tenderState[field] !== undefined) {
            (merged as Record<string, unknown>)[field] = tenderState[field];
            updateField(field, tenderState[field]);
          }
        });
      }

      return { merged, alreadyImported: true, existingStatus };
    } catch (error: any) {
      // 404 just means no existing BOQ for this tender yet - not a failure,
      // same as /tender-info's handleImportProject.
      if (error?.response?.status !== 404) {
        console.error(
          `Error loading imported domain data for ${state.projectName}:`,
          error,
        );
        message.warning({
          content: `Failed to load imported data for "${state.projectName}"`,
          duration: 3,
        });
      }
      return { merged, alreadyImported: false };
    }
  };

  const submitTender = async (
    data: BasicTenderFormData,
    onSuccess: () => void,
  ) => {
    if (validationErrors.length > 0) return;

    const hideLoading = message.loading({
      content: "Saving...",
      key: "saveTender",
      duration: 0,
    });

    try {
      const {
        merged: importedDomainData,
        alreadyImported,
        existingStatus,
      } = await fetchImportedDomainData();

      if (alreadyImported) {
        hideLoading();
        let proceedClicked = false;
        Modal.confirm({
          title: "Tender already imported",
          content: (
            <div>
              <p>
                This opportunity already has an imported tender ("
                {state.projectName}").
              </p>
              {existingStatus && (
                <p style={{ marginTop: 8 }}>
                  <strong>Status:</strong> {existingStatus}
                </p>
              )}
              <p style={{ marginTop: 12 }}>
                Click below to view its details.
              </p>
            </div>
          ),
          okText: "Proceed to Tender",
          cancelText: "Cancel",
          onOk: () => {
            if (proceedClicked) return;
            proceedClicked = true;
            navigate(
              `/version-history/${encodeURIComponent(state.projectName)}${
                state.opportunityNo
                  ? `?oppNo=${encodeURIComponent(state.opportunityNo)}`
                  : ""
              }`,
            );
          },
          onCancel: () => {},
        });
        return;
      }

      // Same "both manually-named job types get concatenated" rule as the
      // debounced auto-save effect above - `data` here is react-hook-form's
      // CURRENT field values (whichever card is active), so on its own it
      // only ever reflects one job type's name, never the concatenation.
      const finalProjectName = bothManualFilled
        ? [effectiveGroundworks!.projectName, effectiveRoadsAndSewers!.projectName]
            .map((s) => s.trim())
            .join(" - ")
        : data.projectName.trim();
      const finalSiteAddress = bothManualFilled
        ? [effectiveGroundworks!.siteAddress, effectiveRoadsAndSewers!.siteAddress]
            .filter(Boolean)
            .map((s) => (s || "").trim())
            .join(" - ")
        : (data.siteAddress || "").trim();

      updateField("projectName", finalProjectName);
      updateField("client", data.client.trim());
      updateField("siteAddress", finalSiteAddress);
      updateField("region", data.region);

      const updatedState: TenderState = {
        ...state,
        ...importedDomainData,
        projectName: finalProjectName,
        client: data.client.trim(),
        siteAddress: finalSiteAddress,
        region: data.region as TenderState["region"],
        status: "In-progress",
        lastModified: new Date(),
      };

      saveTenderToStorage(updatedState);

      const basePayload = prepareTenderForAPI(
        updatedState,
        user?.name || user?.email,
      );

      const completePayload = {
        ...basePayload,
        project: {
          name: finalProjectName,
          client: data.client,
          region: finalSiteAddress
            ? `${finalSiteAddress}~~${data.region}`
            : data.region,
        },
        status: "In-Progress",
        lastModified: new Date().toISOString(),
        tenderCreatedDate:
          state.dateCreated?.toISOString() || new Date().toISOString(),
        calculatedAt: new Date().toISOString(),
        ...(state.opportunityNo && { opportunityNo: state.opportunityNo }),
        // Built fresh here (not read off `state.opportunity`) since that's
        // only synced on card switch/import - a manual entry that's typed
        // into and submitted without ever switching cards would otherwise
        // submit whatever (possibly stale/incomplete) opportunity value was
        // last written, instead of what's actually in the form right now.
        ...(() => {
          const opportunity = buildOpportunityField();
          return opportunity ? { opportunity } : {};
        })(),
      };

      await createBoqMutation.mutateAsync(completePayload);

      // Seed boq-versions with the initial version for each *selected* job type
      // (state.jobTypes, set at the Job Type Selection step) - GWKS-1 for
      // Groundworks, RS-1 for Roads & Sewers, both created in parallel when
      // both are selected. Gate on job-type selection, not on whether an
      // opportunity was actually imported - a manually-entered card (no
      // imported record) still needs its initial version created. Each call
      // uses that job type's own opportunity number when one was imported.
      const versionCalls: Promise<unknown>[] = [];
      if (state.jobTypes.groundworks) {
        versionCalls.push(
          billOfQuantitiesApi.createVersion(
            createVersionPayload({
              projectName: finalProjectName,
              boqPayload: completePayload,
              changeLog: "Initial version - Tender imported",
              version: "GWKS-1",
            }) as any,
            importedGroundworks?.no,
          ),
        );
      }
      if (state.jobTypes.roadsAndSewers) {
        versionCalls.push(
          billOfQuantitiesApi.createVersion(
            createVersionPayload({
              projectName: finalProjectName,
              boqPayload: completePayload,
              changeLog: "Initial version - Tender imported",
              version: "RS-1",
            }) as any,
            importedRoadsAndSewers?.no,
          ),
        );
      }
      if (versionCalls.length > 0) {
        await Promise.all(versionCalls).catch((err) =>
          console.warn("⚠️ BOQ version creation failed (non-blocking):", err),
        );
      }

      itemLibraryApi
        .createSite(data.region, {
          siteName: finalProjectName,
          ...(state.opportunityNo && { oppNo: String(state.opportunityNo) }),
          createdBy: user?.email || user?.name,
        })
        .catch((err) =>
          console.warn("⚠️ Site library creation failed (non-blocking):", err),
        );

      // SharePoint folder creation is commented out here for now - it's
      // handled server-side by the backend on tender creation instead. Do
      // not remove; re-enable by uncommenting if that moves back to the
      // client.
      // let folderCreationFailed = false;
      // try {
      //   const folderResponse = await createFolder(
      //     finalProjectName,
      //     state.opportunityNo,
      //   );
      //   if (
      //     !folderResponse.success &&
      //     folderResponse.error?.code !== "nameAlreadyExists"
      //   ) {
      //     console.warn(
      //       "SharePoint folder creation failed:",
      //       folderResponse.message,
      //     );
      //     folderCreationFailed = true;
      //   }
      // } catch (folderError) {
      //   console.warn("SharePoint folder creation error:", folderError);
      //   folderCreationFailed = true;
      // }

      hideLoading();

      // if (folderCreationFailed) {
      //   message.warning({
      //     content: "Tender saved but folder creation failed",
      //     duration: 3,
      //   });
      // }

      setTimeout(onSuccess, 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      hideLoading();
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        duration: 5,
      });
    }
  };

  const onSubmit = (data: BasicTenderFormData) =>
    submitTender(data, navigateToImportTakeoff);

  const onSaveAndExit = (data: BasicTenderFormData) =>
    submitTender(data, () => navigate("/"));

  const canProceed =
    isValid &&
    Object.keys(errors).length === 0 &&
    validationErrors.length === 0 &&
    !isCreatingFolder;

  // Whichever card is currently active - null when that card has no
  // imported opportunity (manual entry or nothing selected yet).
  const activeImportedRecord =
    activeBasicSource === "roadsAndSewers"
      ? importedRoadsAndSewers
      : activeBasicSource === "groundworks"
        ? importedGroundworks
        : null;

  // The form is only for manual entry - once the active card's opportunity
  // has been imported, its details already show on the card itself, so the
  // form stays hidden until that import is cleared (via the card's close
  // button) or a manual (non-imported) card is selected instead. Manual
  // entry itself is a dev-mode-only affordance (see canSelectGroundworksCard/
  // canSelectRoadsAndSewersCard below), so this branch is also gated on
  // devMode - without it, clearing an already-imported card via its close
  // button would drop a non-dev user into the manual form too, with no way
  // to reach it otherwise (their card isn't selectable to start manual
  // entry in the first place). The "neither job type selected" branch below
  // is NOT dev-gated - it's the fallback for a tender with no job type set
  // at all (e.g. one loaded from before job-type selection existed), where
  // the Import Opportunities cards above never render for anyone, dev or
  // not, so the form is the only way to enter details.
  const showManualForm =
    (devMode && activeBasicSource !== null && !activeImportedRecord) ||
    (!state.jobTypes.roadsAndSewers && !state.jobTypes.groundworks);

  // Manually typing tender details (as opposed to importing an opportunity)
  // is a dev-mode-only affordance - a card is only selectable to START manual
  // entry when devMode is on. An already-imported card, or a card that
  // already carries manual details from a tender loaded before this gate
  // existed, stays selectable for everyone so switching between/viewing
  // existing sources never breaks - only STARTING a fresh manual entry is
  // restricted.
  const canSelectGroundworksCard =
    devMode ||
    !!importedGroundworks ||
    !!manualDetailsByType.groundworks.projectName.trim();
  const canSelectRoadsAndSewersCard =
    devMode ||
    !!importedRoadsAndSewers ||
    !!manualDetailsByType.roadsAndSewers.projectName.trim();

  // Named after whichever job type(s) are actually on this tender, so a
  // single-job-type tender is prompted by name instead of the generic
  // "Roads & Sewers or Groundworks" phrasing that only makes sense once both
  // are selected.
  const pendingOpportunityLabel = bothJobTypesSelected
    ? "a Roads & Sewers or Groundworks"
    : state.jobTypes.roadsAndSewers
      ? "the Roads & Sewers"
      : "the Groundworks";

  // Same naming rule for the "Import Opportunities" helper text above the
  // cards - "for each selected job type" only reads naturally once both are
  // selected, otherwise name the one that is.
  const importOpportunityPhrase = bothJobTypesSelected
    ? "Import an existing opportunity for each selected job type"
    : `Import an existing ${state.jobTypes.roadsAndSewers ? "Roads & Sewers" : "Groundworks"} opportunity`;

  return (
    <MainLayout canProceed={canProceed} showFooter={false}>
      <div className="max-w-4xl mx-auto">
        {!isRoleLoading && role && role !== "Estimator" && (
          <Alert
            type="info"
            showIcon
            message="Authorized users have the ability to create tenders, while submitting and approving them is governed by role-based permissions assigned to Estimators, Senior Estimators, and the Commercial Director."
            className="mb-4"
          />
        )}
        <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
          <div
            className={`px-6 pb-6 ${
              state.jobTypes.roadsAndSewers || state.jobTypes.groundworks
                ? ""
                : "pt-6"
            }`}
          >
            {(state.jobTypes.roadsAndSewers || state.jobTypes.groundworks) && (
              <div className="mb-6">
                <Title
                  level={5}
                  className="mb-4"
                  style={{ fontWeight: 600, fontSize: "16px", color: "#111827" }}
                >
                  Import Opportunities
                </Title>
                <Text
                  className="block mb-4"
                  style={{ color: "#6b7280", fontSize: "13px" }}
                >
                  {devMode
                    ? `${importOpportunityPhrase}, or skip and enter the tender details manually below.`
                    : `${importOpportunityPhrase} below.`}
                </Text>
                <Row gutter={[24, 24]}>
                  {state.jobTypes.groundworks && (
                    <Col
                      xs={24}
                      md={state.jobTypes.roadsAndSewers ? 12 : 24}
                    >
                      <OpportunityImportCard
                        title="Groundworks Opportunity"
                        description="Foundations, external works, private drainage, additional items."
                        icon={
                          <Layers3 size={24} color="#9333EA" />
                        }
                        accentColor="#9333EA"
                        backgroundColor="#F3E8FF"
                        opportunities={opportunities}
                        opportunitiesLoading={opportunitiesLoading}
                        userRegion={userRegion}
                        imported={importedGroundworks}
                        onImport={(record) =>
                          handleImportOpportunity("groundworks", record)
                        }
                        onClear={() => handleClearCard("groundworks")}
                        isActiveSource={activeBasicSource === "groundworks"}
                        onSelectAsSource={
                          canSelectGroundworksCard
                            ? () => handleSwitchBasicSource("groundworks")
                            : undefined
                        }
                        manualPreview={
                          !importedGroundworks
                            ? activeBasicSource === "groundworks"
                              ? {
                                  projectName: watchedFields.projectName,
                                  client: watchedFields.client,
                                  siteAddress: watchedFields.siteAddress,
                                  region: watchedFields.region,
                                }
                              : manualDetailsByType.groundworks
                            : undefined
                        }
                      />
                    </Col>
                  )}
                  {state.jobTypes.roadsAndSewers && (
                    <Col
                      xs={24}
                      md={state.jobTypes.groundworks ? 12 : 24}
                    >
                      <OpportunityImportCard
                        title="Roads & Sewers Opportunity"
                        description="Foul/storm sewers, road construction, main services, preliminaries."
                        icon={
                          <Route size={24} color={COLORS.primary} />
                        }
                        accentColor={COLORS.primary}
                        backgroundColor="#E8F4FD"
                        opportunities={opportunities}
                        opportunitiesLoading={opportunitiesLoading}
                        userRegion={userRegion}
                        imported={importedRoadsAndSewers}
                        onImport={(record) =>
                          handleImportOpportunity("roadsAndSewers", record)
                        }
                        onClear={() => handleClearCard("roadsAndSewers")}
                        isActiveSource={activeBasicSource === "roadsAndSewers"}
                        onSelectAsSource={
                          canSelectRoadsAndSewersCard
                            ? () => handleSwitchBasicSource("roadsAndSewers")
                            : undefined
                        }
                        manualPreview={
                          !importedRoadsAndSewers
                            ? activeBasicSource === "roadsAndSewers"
                              ? {
                                  projectName: watchedFields.projectName,
                                  client: watchedFields.client,
                                  siteAddress: watchedFields.siteAddress,
                                  region: watchedFields.region,
                                }
                              : manualDetailsByType.roadsAndSewers
                            : undefined
                        }
                      />
                    </Col>
                  )}
                </Row>
              </div>
            )}

            {!showManualForm && (
              <Alert
                type="info"
                showIcon
                message={
                  activeImportedRecord
                    ? devMode
                      ? "Opportunity imported - details are shown on the card above. Click the close (×) button on the card to clear it and enter details manually instead."
                      : "Opportunity imported - details are shown on the card above. Click the close (×) button on the card to clear it and import a different opportunity."
                    : devMode
                      ? `Select ${pendingOpportunityLabel} opportunity card above to reveal the tender details form.`
                      : `Import ${pendingOpportunityLabel} opportunity above - the details will be shown on the form here once imported.`
                }
                className="mb-6"
              />
            )}

            {/* Always mounted (even while hidden) so every field's Controller is
                registered from the first render - setValue on import/switch must
                never race a field's own mount. */}
            <form
              onSubmit={handleSubmit(onSubmit)}
              // Both real submit actions (Save and Exit / Save & Continue)
              // are triggered explicitly by the two buttons below, outside
              // this <form> - but a plain HTML <form> with multiple text
              // inputs still submits natively on Enter (invoking this
              // onSubmit, i.e. always the "Save & Continue" path) regardless
              // of which field the user was in or which button they meant to
              // press. That silently created a stray, incompletely-filled
              // tender (e.g. pressing Enter after typing just the Roads &
              // Sewers card's name) alongside the one actually created by a
              // deliberate button click. Blocking Enter here doesn't affect
              // either button's own onClick-driven handleSubmit() call.
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              style={{ display: showManualForm ? undefined : "none" }}
            >
              {/* Form Section */}
              <div className="mb-6">
                <Title
                  level={5}
                  className="mb-4"
                  style={{ fontWeight: 600, fontSize: "16px", color: "#111827" }}
                >
                  Tender Information
                </Title>

                <Row gutter={[24, 24]}>
                  {/* Project Name */}
                  <Col xs={24} md={12}>
                    <div className="space-y-1">
                      <Text
                        className="block"
                        style={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#374151",
                          marginBottom: "6px",
                        }}
                      >
                        Tender Name <span style={{ color: "#ef4444" }}>*</span>
                      </Text>
                      <Controller
                        name="projectName"
                        control={control}
                        rules={{
                          required: "Project name is required",
                          minLength: {
                            value: 3,
                            message:
                              "Project name must be at least 3 characters",
                          },
                          validate: (value) =>
                            value.trim().length > 0 ||
                            "Project name cannot be blank",
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="Enter project name"
                            status={errors.projectName ? "error" : undefined}
                            onBlur={() => {
                              field.onBlur();
                              validateProjectName(field.value);
                            }}
                          />
                        )}
                      />
                      {errors.projectName && (
                        <Text type="danger" className="text-xs">
                          {errors.projectName.message}
                        </Text>
                      )}
                    </div>
                  </Col>

                  {/* Client Name */}
                  <Col xs={24} md={12}>
                    <div className="space-y-1">
                      <Text
                        className="block"
                        style={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#374151",
                          marginBottom: "6px",
                        }}
                      >
                        Client Name <span style={{ color: "#ef4444" }}>*</span>
                      </Text>
                      <Controller
                        name="client"
                        control={control}
                        rules={{
                          required: "Client name is required",
                          validate: (value) =>
                            value.trim().length > 0 ||
                            "Client name cannot be blank",
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="Enter client name"
                            status={errors.client ? "error" : undefined}
                          />
                        )}
                      />
                      {errors.client && (
                        <Text type="danger" className="text-xs">
                          {errors.client.message}
                        </Text>
                      )}
                    </div>
                  </Col>

                  {/* Site Address */}
                  <Col xs={24} md={12}>
                    <div className="space-y-1">
                      <Text
                        className="block"
                        style={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#374151",
                          marginBottom: "6px",
                        }}
                      >
                        Site Address <span style={{ color: "#ef4444" }}>*</span>
                      </Text>
                      <Controller
                        name="siteAddress"
                        control={control}
                        rules={{
                          // Only enforced for manual entry (showManualForm) -
                          // an imported opportunity's own siteAddress may be
                          // blank, and the user must still be able to
                          // Save/Continue with whatever the opportunity
                          // actually supplied instead of being blocked here.
                          required: showManualForm
                            ? "Site address is required"
                            : false,
                          validate: (value) =>
                            !showManualForm ||
                            value.trim().length > 0 ||
                            "Site address cannot be blank",
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="Enter site address"
                            status={errors.siteAddress ? "error" : undefined}
                          />
                        )}
                      />
                      {errors.siteAddress && (
                        <Text type="danger" className="text-xs">
                          {errors.siteAddress.message}
                        </Text>
                      )}
                    </div>
                  </Col>

                  {/* Region */}
                  <Col xs={24} md={12}>
                    <div className="space-y-1">
                      <Text
                        className="block"
                        style={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#374151",
                          marginBottom: "6px",
                        }}
                      >
                        Region <span style={{ color: "#ef4444" }}>*</span>
                      </Text>
                      <Controller
                        name="region"
                        control={control}
                        rules={{
                          // Same reasoning as siteAddress above - only
                          // required for manual entry, not for an imported
                          // opportunity whose own region may be blank.
                          required: showManualForm
                            ? "Region is required"
                            : false,
                        }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            value={field.value || undefined}
                            placeholder="Select region"
                            className="w-full"
                            status={errors.region ? "error" : undefined}
                            disabled={
                              !!(userRegion && userRegion !== "All Regions")
                            }
                            options={REGIONS.map((region) => ({
                              label: region,
                              value: region,
                            }))}
                          />
                        )}
                      />
                      {errors.region && (
                        <Text type="danger" className="text-xs">
                          {errors.region.message}
                        </Text>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <Alert
                  type="error"
                  className="mb-6"
                  message={
                    <div>
                      <Text className="font-semibold">
                        Please fix the following errors:
                      </Text>
                      <ul className="mt-2 ml-4 list-disc">
                        {validationErrors.map((error, index) => (
                          <li key={index}>
                            <Text className="text-sm">{error}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  }
                />
              )}

              {/* Date Information */}
              {state.projectName && state.projectName.trim() !== "" && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarOutlined style={{ color: "#6b7280", fontSize: "14px" }} />
                    <Text style={{ color: "#6b7280", fontSize: "14px" }}>
                      Date Created:{" "}
                      <span style={{ fontWeight: 600, color: "#374151" }}>
                        {format(state.dateCreated, "MMMM do, yyyy h:mm a")}
                      </span>
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarOutlined style={{ color: "#6b7280", fontSize: "14px" }} />
                    <Text style={{ color: "#6b7280", fontSize: "14px" }}>
                      Last Modified:{" "}
                      <span style={{ fontWeight: 600, color: "#374151" }}>
                        {format(state.lastModified, "MMMM do, yyyy h:mm a")}
                      </span>
                    </Text>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Action Buttons Section */}
          <div style={{ borderTop: "1px solid #e5e7eb" }}>
            <div className="px-6 py-4 flex justify-end gap-3">
              <Button
                onClick={canProceed ? handleSubmit(onSaveAndExit) : undefined}
                style={
                  !canProceed
                    ? {
                        borderColor: "#d9d9d9",
                        color: "rgba(0,0,0,0.25)",
                        backgroundColor: "#fff",
                        cursor: "not-allowed",
                      }
                    : {
                        borderColor: COLORS.primary,
                        color: COLORS.primary,
                      }
                }
              >
                Save and Exit
              </Button>
              <Button
                type="primary"
                htmlType="button"
                onClick={canProceed ? handleSubmit(onSubmit) : undefined}
                style={
                  !canProceed
                    ? {
                        backgroundColor: "rgba(0,0,0,0.04)",
                        borderColor: "#d9d9d9",
                        color: "rgba(0,0,0,0.25)",
                        cursor: "not-allowed",
                      }
                    : {
                        backgroundColor: COLORS.primary,
                        borderColor: COLORS.primary,
                      }
                }
              >
                Save & Continue <ArrowRightOutlined />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default TenderDetails;
